/**
 * Question bank explorer — the SAME structure as the Courses explorer
 * (Category → Course → Chapter → Topic → Subtopic), carrying only what holds questions:
 * quizzes / exams sit where they live in the course, lesson content pages are left out unless a
 * quiz sits underneath them, and exams belonging to no course appear at their pushed location.
 *
 *   const bank = useBankTree({ api });
 *   bank.nodes · bank.reload() · bank.byKey(key) → { kind, node, category, course, descendants }
 *
 * Node keys: cat:<key> · course:<id> · node:<id> · exam:<id> · unlinked
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Folder, FolderOpen, Books, BookOpen, Bookmarks, File, Exam, Question } from '@phosphor-icons/react';

export const EXAM_TYPE_LABEL = { quiz: 'Quiz', exam: 'Exam', practice: 'Practice test', survey: 'Survey', feedback: 'Feedback' };
const NODE_LABEL = { section: 'Chapter', subsection: 'Topic' };

/** A quiz / exam that is not part of a course structure (pushed to the bank on its own). */
const examNode = (e) => ({
  key: `exam:${e.id}`, label: e.title, icon: Exam, iconClass: e.is_published ? 'text-violet-600' : 'text-muted-foreground', kind: 'exam', data: e, muted: !e.is_published,
  title: `${EXAM_TYPE_LABEL[e.assessment_type] || e.assessment_type}: ${e.title}${e.is_published ? '' : ' — draft'}`,
  badge: e.question_count || undefined, children: [],
});

function nodeIcon(n) {
  if (n.is_quiz) return [Exam, n.is_published === false ? 'text-muted-foreground' : 'text-violet-600'];
  if (n.node_type === 'section') return [BookOpen, 'text-amber-600'];
  if (n.node_type === 'subsection') return [Bookmarks, 'text-sky-600'];
  return [File, 'text-slate-500'];
}

function buildNodes(nodes) {
  const byParent = {};
  nodes.forEach((n) => { (byParent[n.parent_id || '__root'] = byParent[n.parent_id || '__root'] || []).push(n); });
  // a kept node whose parent was pruned hangs off the course directly
  const ids = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => !n.parent_id || !ids.has(n.parent_id)).map((n) => n.id);
  const descendantIds = (id) => (byParent[id] || []).flatMap((c) => [c.id, ...descendantIds(c.id)]);
  const build = (list) => list.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map((n) => {
    const [icon, iconClass] = nodeIcon(n);
    return {
      key: `node:${n.id}`, label: n.title, kind: n.is_quiz ? 'quiz-node' : 'node', data: n, icon, iconClass,
      descendantIds: descendantIds(n.id), muted: n.is_quiz && n.is_published === false,
      title: n.is_quiz ? `${EXAM_TYPE_LABEL[n.assessment_type] || 'Quiz'}: ${n.title}${n.is_published === false ? ' — draft' : ''}` : `${NODE_LABEL[n.node_type] || 'Subtopic'}: ${n.title}`,
      badge: n.question_count || undefined,
      children: [...build(byParent[n.id] || []), ...(n.exams || []).map(examNode)],
    };
  });
  return build(nodes.filter((n) => roots.includes(n.id)));
}

export default function useBankTree({ api, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try { setData((await api.get('/catalog/bank-tree')).data); }
    catch { setData((d) => d || { categories: [] }); }
    finally { setLoading(false); }
  }, [api, enabled]);
  useEffect(() => { reload(); }, [reload]);

  const nodes = useMemo(() => {
    const courseNode = (c) => ({
      key: `course:${c.id}`, label: c.title, icon: Books, iconClass: c.is_published ? 'text-shell-primary' : 'text-muted-foreground', kind: 'course', data: c, muted: !c.is_published,
      title: `${c.title}${c.course_code ? ` (${c.course_code})` : ''}${c.is_published ? '' : ' — draft'}`,
      badge: c.question_count || undefined,
      children: [...buildNodes(c.nodes || []), ...(c.exams || []).map(examNode)],
    });
    const catNode = (cat) => ({
      key: `cat:${cat.key}`, label: cat.label, icon: cat.children?.length ? FolderOpen : Folder, iconClass: 'text-amber-500', kind: 'category', data: cat,
      title: cat.path && cat.path !== cat.label ? cat.path : undefined,
      badge: cat.question_count || undefined,
      children: [...(cat.children || []).map(catNode), ...(cat.courses || []).map(courseNode), ...(cat.exams || []).map(examNode)],
    });
    const out = (data?.categories || []).map(catNode);
    if (data?.unfiled_exams?.length) {
      out.push({ key: 'unfiled-exams', label: 'Quizzes & exams without a place', icon: Exam, iconClass: 'text-muted-foreground', kind: 'unfiled-exams', muted: true, badge: data.unfiled_exams.length, children: data.unfiled_exams.map(examNode) });
    }
    if (data?.unfiled_question_count) {
      out.push({ key: 'unlinked', label: 'Questions not linked to a course', icon: Question, iconClass: 'text-muted-foreground', kind: 'unlinked', badge: data.unfiled_question_count, children: [], muted: true });
    }
    return out;
  }, [data]);

  const index = useMemo(() => {
    const map = {};
    const walk = (list, cat, course) => list.forEach((n) => {
      map[n.key] = { node: n, kind: n.kind, category: cat, course };
      if (n.kind === 'category') walk(n.children, n, null);
      else if (n.kind === 'course') walk(n.children, cat, n);
      else walk(n.children, cat, course);
    });
    walk(nodes, null, null);
    return map;
  }, [nodes]);

  const byKey = useCallback((key) => {
    const e = index[key];
    if (!e) return null;
    const descendants = [];
    const walk = (n) => n.children?.forEach((c) => { descendants.push(c); walk(c); });
    walk(e.node);
    return { ...e, descendants };
  }, [index]);

  return { nodes, data, loading, reload, byKey };
}
